import React, { useState } from 'react';
import styled from 'styled-components';

const SupportContainer = styled.div`
  padding: 2rem 0;
`;

const PageTitle = styled.h1`
  color: var(--dark);
  margin-bottom: 1.5rem;
  font-family: 'Montserrat', sans-serif;
`;

const SupportCard = styled.div`
  background-color: white;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  max-width: 800px;
  margin: 0 auto;
`;

const SupportHeader = styled.div`
  background-color: var(--primary);
  padding: 2rem;
  text-align: center;
  color: white;
`;

const SupportTitle = styled.h2`
  margin-bottom: 0.5rem;
  font-family: 'Montserrat', sans-serif;
`;

const SupportSubtitle = styled.p`
  font-size: 1.1rem;
  opacity: 0.9;
  margin-bottom: 0;
`;

const SupportContent = styled.div`
  padding: 2rem;
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const FormLabel = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: var(--dark);
`;

const FormInput = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  transition: border-color 0.3s ease;
  
  &:focus {
    border-color: var(--primary);
    outline: none;
    box-shadow: 0 0 0 3px rgba(233, 64, 87, 0.1);
  }
  
  ${props => props.error && `
    border-color: #dc3545;
    
    &:focus {
      box-shadow: 0 0 0 3px rgba(220, 53, 69, 0.1);
    }
  `}
`;

const FormSelect = styled.select`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  transition: border-color 0.3s ease;
  
  &:focus {
    border-color: var(--primary);
    outline: none;
    box-shadow: 0 0 0 3px rgba(233, 64, 87, 0.1);
  }
`;

const FormTextarea = styled.textarea`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  transition: border-color 0.3s ease;
  min-height: 150px;
  resize: vertical;
  
  &:focus {
    border-color: var(--primary);
    outline: none;
    box-shadow: 0 0 0 3px rgba(233, 64, 87, 0.1);
  }
  
  ${props => props.error && `
    border-color: #dc3545;
    
    &:focus {
      box-shadow: 0 0 0 3px rgba(220, 53, 69, 0.1);
    }
  `}
`;

const ErrorMessage = styled.div`
  color: #dc3545;
  font-size: 0.85rem;
  margin-top: 0.5rem;
`;

const HelperText = styled.div`
  color: var(--gray);
  font-size: 0.85rem;
  margin-top: 0.5rem;
`;

const SubmitButton = styled.button`
  background-color: var(--primary);
  color: white;
  border: none;
  border-radius: 8px;
  padding: 0.75rem 1.5rem;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.3s ease, transform 0.2s ease;
  
  &:hover {
    background-color: var(--primary-dark);
    transform: translateY(-2px);
  }
  
  &:disabled {
    background-color: #e0e0e0;
    cursor: not-allowed;
    transform: none;
  }
  
  .spinner {
    margin-right: 0.5rem;
    animation: spin 1s linear infinite;
  }
  
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const SuccessMessage = styled.div`
  background-color: #d4edda;
  color: #155724;
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  
  .icon {
    font-size: 1.5rem;
    margin-right: 0.75rem;
  }
`;

const SupportInfo = styled.div`
  margin-top: 3rem;
  background-color: var(--light);
  border-radius: 8px;
  padding: 1.5rem;
`;

const SupportInfoTitle = styled.h3`
  color: var(--dark);
  margin-bottom: 1rem;
  font-family: 'Montserrat', sans-serif;
`;

const SupportContact = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 1rem;
  
  &:last-child {
    margin-bottom: 0;
  }
  
  .icon {
    width: 40px;
    height: 40px;
    background-color: var(--primary-light);
    color: white;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-right: 1rem;
    font-size: 1.25rem;
  }
  
  .contact-info {
    flex-grow: 1;
  }
  
  .contact-label {
    color: var(--gray);
    font-size: 0.9rem;
  }
  
  .contact-value {
    color: var(--dark);
    font-weight: 500;
  }
`;

const SupportPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    childName: '',
    childAge: '',
    subject: '',
    message: ''
  });
  
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };
  
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    
    if (!formData.subject.trim()) {
      newErrors.subject = 'Subject is required';
    }
    
    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    }
    
    return newErrors;
  };
  
  const handleSubmit = (e) => {
    e.preventDefault();
    
    const formErrors = validateForm();
    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
      return;
    }
    
    setIsSubmitting(true);
    
    // Simulate API call
    setTimeout(() => {
      console.log('Support request submitted:', formData);
      setIsSubmitting(false);
      setIsSubmitted(true);
      
      // Reset form
      setFormData({
        name: '',
        email: '',
        childName: '',
        childAge: '',
        subject: '',
        message: ''
      });
    }, 1500);
  };
  
  return (
    <div className="container">
      <SupportContainer>
        <PageTitle>Support</PageTitle>
        <p className="lead mb-4">
          Need help with your sleep plan or have questions about our service? We're here to help!
        </p>
        
        <SupportCard>
          <SupportHeader>
            <SupportTitle>Contact Our Support Team</SupportTitle>
            <SupportSubtitle>We typically respond within 24 hours</SupportSubtitle>
          </SupportHeader>
          
          <SupportContent>
            {isSubmitted && (
              <SuccessMessage>
                <i className="bi bi-check-circle-fill icon"></i>
                <div>
                  <strong>Thank you for reaching out!</strong> Your message has been received. Our team will get back to you within 24 hours.
                </div>
              </SuccessMessage>
            )}
            
            <form onSubmit={handleSubmit}>
              <FormGroup>
                <FormLabel htmlFor="name">Your Name *</FormLabel>
                <FormInput
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  error={errors.name}
                />
                {errors.name && <ErrorMessage>{errors.name}</ErrorMessage>}
              </FormGroup>
              
              <FormGroup>
                <FormLabel htmlFor="email">Email Address *</FormLabel>
                <FormInput
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  error={errors.email}
                />
                {errors.email && <ErrorMessage>{errors.email}</ErrorMessage>}
              </FormGroup>
              
              <div className="row">
                <div className="col-md-6">
                  <FormGroup>
                    <FormLabel htmlFor="childName">Child's Name (optional)</FormLabel>
                    <FormInput
                      type="text"
                      id="childName"
                      name="childName"
                      value={formData.childName}
                      onChange={handleChange}
                    />
                  </FormGroup>
                </div>
                <div className="col-md-6">
                  <FormGroup>
                    <FormLabel htmlFor="childAge">Child's Age (optional)</FormLabel>
                    <FormSelect
                      id="childAge"
                      name="childAge"
                      value={formData.childAge}
                      onChange={handleChange}
                    >
                      <option value="">Select age</option>
                      <option value="0-3 months">0-3 months</option>
                      <option value="4-6 months">4-6 months</option>
                      <option value="7-12 months">7-12 months</option>
                      <option value="1-2 years">1-2 years</option>
                      <option value="2-3 years">2-3 years</option>
                      <option value="3-5 years">3-5 years</option>
                    </FormSelect>
                  </FormGroup>
                </div>
              </div>
              
              <FormGroup>
                <FormLabel htmlFor="subject">Subject *</FormLabel>
                <FormInput
                  type="text"
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  error={errors.subject}
                />
                {errors.subject && <ErrorMessage>{errors.subject}</ErrorMessage>}
              </FormGroup>
              
              <FormGroup>
                <FormLabel htmlFor="message">Message *</FormLabel>
                <FormTextarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  error={errors.message}
                  placeholder="Please describe your question or issue in detail"
                />
                {errors.message && <ErrorMessage>{errors.message}</ErrorMessage>}
                <HelperText>
                  Include any relevant details about your sleep plan or specific challenges you're facing.
                </HelperText>
              </FormGroup>
              
              <div className="text-end">
                <SubmitButton type="submit" disabled={isSubmitting}>
                  {isSubmitting ? (
                    <>
                      <span className="spinner">⟳</span> Sending...
                    </>
                  ) : (
                    'Send Message'
                  )}
                </SubmitButton>
              </div>
            </form>
          </SupportContent>
        </SupportCard>
        
        <SupportInfo>
          <SupportInfoTitle>Other Ways to Reach Us</SupportInfoTitle>
          
          <SupportContact>
            <div className="icon">
              <i className="bi bi-envelope-fill"></i>
            </div>
            <div className="contact-info">
              <div className="contact-label">Email</div>
              <div className="contact-value">support@dozzy.au</div>
            </div>
          </SupportContact>
          
          <SupportContact>
            <div className="icon">
              <i className="bi bi-telephone-fill"></i>
            </div>
            <div className="contact-info">
              <div className="contact-label">Phone</div>
              <div className="contact-value">1300 DOZZY AU (1300 369 928)</div>
            </div>
          </SupportContact>
          
          <SupportContact>
            <div className="icon">
              <i className="bi bi-clock-fill"></i>
            </div>
            <div className="contact-info">
              <div className="contact-label">Support Hours</div>
              <div className="contact-value">Monday to Friday, 9am - 5pm AEST</div>
            </div>
          </SupportContact>
        </SupportInfo>
      </SupportContainer>
    </div>
  );
};

export default SupportPage;
